import { forwardRef, memo } from "react";
import Moment from "react-moment";
import { formatCurrency } from "./SCRV2InvoiceDetailHelpers";
import { renderScrV2IdentityDate } from "./SCRV2IdentityPresentation";
import { ScrV2PrintKpi as PrintKpi } from "./SCRV2SummaryReportPrintComponents";
import { SCR_V2_COMPLETE_DETAIL_PRINT_STYLES } from "./SCRV2CompleteDetailPrintStyles";
import { PrintInvoiceActivityBadges } from "./SCRV2InvoiceActivityBadges";

const PrintInvoiceTable = ({ rows }) => (
  <table className="scr-v2-print-invoice-table">
    <thead>
      <tr>
        <th style={{ width: "14%" }}>VizShop Code</th>
        <th style={{ width: "28%" }}>Shop Name</th>
        <th style={{ width: "18%" }}>Invoice No.</th>
        <th className="scr-v2-print-num" style={{ width: "14%" }}>
          Invoice Amount
        </th>
        <th className="scr-v2-print-num" style={{ width: "14%" }}>
          Received Amount
        </th>
        <th style={{ width: "12%" }}>Invoice Date</th>
      </tr>
    </thead>
    <tbody>
      {!rows?.length ? (
        <tr>
          <td colSpan="6" className="scr-v2-print-invoice-table__empty">
            No invoices found.
          </td>
        </tr>
      ) : (
        rows.map((row) => (
          <tr key={row.key}>
            <td>{row.vizShopCode || "-"}</td>
            <td>{row.shopName || "-"}</td>
            <td className="scr-v2-print-invoice-no">
              {row.invoiceNo || "-"}
              <PrintInvoiceActivityBadges
                sectionActivityBadge={row.sectionActivityBadge}
                sectionId={row.sectionId}
              />
            </td>
            <td className="scr-v2-print-num">{formatCurrency(row.invoiceAmount)}</td>
            <td className="scr-v2-print-num">{formatCurrency(row.receivedAmount)}</td>
            <td>
              {row.invoiceDate ? <Moment format="DD-MM-YYYY">{row.invoiceDate}</Moment> : "-"}
            </td>
          </tr>
        ))
      )}
    </tbody>
  </table>
);

const SCRV2CompleteDetailPrint = forwardRef(({ report, highlights, sections, searchText }, ref) => {
  if (!report?.scrId) {
    return <div ref={ref} />;
  }

  const printedAt = new Date();
  const trimmedSearch = (searchText || "").trim();
  const sectionList = Array.isArray(sections) ? sections : [];

  return (
    <div ref={ref} className="scr-v2-complete-detail-print-view scr-v2-summary-print-view">
      <style>{SCR_V2_COMPLETE_DETAIL_PRINT_STYLES}</style>

      <div className="scr-v2-print-header">
        <h1 className="scr-v2-print-header__title">SCR Invoice Wise Detail</h1>
        <div className="scr-v2-print-header__meta">
          SCR No: <strong>{report.scrNo || "-"}</strong>
        </div>
      </div>

      <div className="scr-v2-print-info-strip">
        <div className="scr-v2-print-info-item">
          <span className="scr-v2-print-info-item__label">Distributor</span>
          <span className="scr-v2-print-info-item__value">{report.distributorName || "-"}</span>
        </div>
        <div className="scr-v2-print-info-item">
          <span className="scr-v2-print-info-item__label">Distribution Center</span>
          <span className="scr-v2-print-info-item__value">{report.distCenterName || "-"}</span>
        </div>
        <div className="scr-v2-print-info-item">
          <span className="scr-v2-print-info-item__label">DLM Details</span>
          <span className="scr-v2-print-info-item__value">
            {report.dlmName || report.dlmUserName || "-"}
          </span>
          {report.deliveryQRCode ? (
            <span className="scr-v2-print-info-item__sub">{report.deliveryQRCode}</span>
          ) : null}
        </div>
        <div className="scr-v2-print-info-item">
          <span className="scr-v2-print-info-item__label">Date</span>
          <span className="scr-v2-print-info-item__value">
            {renderScrV2IdentityDate(report.businessDate)}
          </span>
          {report.finalizedDate ? (
            <span className="scr-v2-print-info-item__sub">
              Finalized: {renderScrV2IdentityDate(report.finalizedDate)}
            </span>
          ) : null}
        </div>
      </div>

      {trimmedSearch ? (
        <div className="scr-v2-print-filter-note">
          Search filter: <strong>{trimmedSearch}</strong>
        </div>
      ) : null}

      <div className="scr-v2-print-kpi-row">
        <PrintKpi
          title="Total Payment Due"
          amount={formatCurrency(highlights?.totalDue)}
          detail={`${highlights?.totalInvoices || 0} invoice(s) total`}
        />
        <PrintKpi
          title="Total Payment Received"
          amount={formatCurrency(highlights?.totalReceived)}
          detail={`${highlights?.collectionRate || 0}% collection rate`}
        />
        <PrintKpi
          title={`${highlights?.varianceLabel || "Variance"} (Variance)`}
          amount={formatCurrency(Math.abs(Number(highlights?.variance || 0)))}
          detail={
            Number(highlights?.variance || 0) === 0
              ? "Fully balanced"
              : `${highlights?.varianceLabel || "Variance"} amount`
          }
        />
      </div>

      {sectionList.length === 0 ? (
        <div className="scr-v2-print-invoice-section">
          <div className="scr-v2-print-invoice-section__header">
            <span className="scr-v2-print-invoice-section__label">Invoices</span>
          </div>
          <table className="scr-v2-print-invoice-table">
            <tbody>
              <tr>
                <td className="scr-v2-print-invoice-table__empty">No invoices found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        sectionList.map((section) => (
          <div key={section.id} className="scr-v2-print-invoice-section">
            <div className="scr-v2-print-invoice-section__header">
              <span className="scr-v2-print-invoice-section__title">
                <span className="scr-v2-print-invoice-section__label">{section.title}</span>
                <span className="scr-v2-print-invoice-section__count">
                  {(section.filteredRows || []).length}
                </span>
                {Number(section.totalAmount || 0) > 0 ? (
                  <span className="scr-v2-print-invoice-section__amount">
                    {formatCurrency(section.totalAmount)}
                  </span>
                ) : null}
              </span>
            </div>
            <PrintInvoiceTable rows={section.filteredRows} />
          </div>
        ))
      )}

      <div className="scr-v2-print-footer">
        Printed: <Moment format="DD-MM-YYYY HH:mm">{printedAt}</Moment>
      </div>
    </div>
  );
});

SCRV2CompleteDetailPrint.displayName = "SCRV2CompleteDetailPrint";

export default memo(SCRV2CompleteDetailPrint);
